import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api from "@/lib/api";
import { Sparkles, Bookmark, ClipboardList, Grid3x3 } from "lucide-react";

const TABS = [
  { key: "new", label: "New", icon: <Sparkles size={13} /> },
  { key: "all", label: "All Collections", icon: <Grid3x3 size={13} /> },
  { key: "requests", label: "My Requests", icon: <ClipboardList size={13} /> },
  { key: "saved", label: "Saved", icon: <Bookmark size={13} /> },
];

export default function MemberCollectionsPage() {
  const [tab, setTab] = useState("new");
  const [collections, setCollections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/collections"),
        api.get("/my-catalog-requests"),
      ]);
      setCollections(c.data || []);
      setRequests(r.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cutoff = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 21); return d.toISOString();
  }, []);

  const filtered = collections.filter((c) => {
    if (tab === "new") return (c.published_at || "") >= cutoff;
    return true;  // "all" tab
  });

  return (
    <>
      <NavBar />
      <main className="max-w-6xl mx-auto px-5 sm:px-10 py-10">
        <section data-testid="member-collections-page">
          <div className="overline">Collections</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-2">Curated by Your Club</h1>
          <p className="mt-2 text-sm text-[#4A4A4A] max-w-2xl">
            A private digital showroom of pieces your pro shop has selected for members.
          </p>

          <div className="mt-6 flex flex-wrap gap-2 border-b border-[#E5E3D8]" data-testid="member-collections-tabs">
            {TABS.map((t) => (
              <button
                key={t.key} onClick={() => setTab(t.key)}
                data-testid={`member-collections-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.24em] font-semibold border-b-2 -mb-px transition-colors ${
                  tab === t.key ? "border-[#1A422D] text-[#1A422D]" : "border-transparent text-[#7A7A7A] hover:text-[#1A422D]"
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mt-8 text-sm text-[#7A7A7A]">Loading…</div>
          ) : tab === "requests" ? (
            <RequestsList requests={requests} />
          ) : filtered.length === 0 ? (
            <div className="mt-10 p-10 border border-dashed border-[#B59A5A]/40 text-center" data-testid="member-collections-empty">
              <Grid3x3 className="mx-auto text-[#B59A5A]" size={26} />
              <div className="mt-3 font-serif text-2xl text-[#1A422D]">Nothing here yet</div>
              <p className="text-sm text-[#4A4A4A] mt-2">
                When your pro shop publishes a collection, it will appear here.
              </p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="member-collections-list">
              {filtered.map((c) => (
                <Link
                  key={c.collection_id}
                  to={`/collections/${c.collection_id}`}
                  data-testid={`member-collection-card-${c.collection_id}`}
                  className="p-5 border border-[#E5E3D8] bg-white hover:border-[#B59A5A] transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
                    New Collection
                  </div>
                  <div className="mt-1 font-serif text-xl text-[#1A422D]">{c.name}</div>
                  {c.description && (
                    <div className="mt-2 text-xs text-[#4A4A4A] line-clamp-3">{c.description}</div>
                  )}
                  <div className="mt-3 text-xs text-[#7A7A7A]">
                    {c.product_refs?.length || 0} products
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function RequestsList({ requests }) {
  if (requests.length === 0) {
    return (
      <div className="mt-10 p-10 border border-dashed border-[#B59A5A]/40 text-center" data-testid="member-requests-empty">
        <ClipboardList className="mx-auto text-[#B59A5A]" size={26} />
        <div className="mt-3 font-serif text-2xl text-[#1A422D]">No requests yet</div>
        <p className="text-sm text-[#4A4A4A] mt-2">Requests you submit from a collection will appear here.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]" data-testid="member-requests-list">
      {requests.map((r) => (
        <div key={r.request_id} className="p-5 flex items-center justify-between gap-4" data-testid={`member-request-row-${r.request_id}`}>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">{r.status}</div>
            <div className="text-[#1A422D] font-medium mt-1">{r.product?.product_name || r.product_id}</div>
            <div className="text-xs text-[#7A7A7A] mt-1">{r.size || "?"} / {r.color || "?"} · Qty {r.quantity}</div>
          </div>
          <div className="text-xs text-[#7A7A7A]">{r.created_at?.slice(0, 10)}</div>
        </div>
      ))}
    </div>
  );
}
